import React, { useState, useEffect } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField,
    FormControl, InputLabel, Select, MenuItem, Box, Typography
} from '@mui/material';
import { PayrollService } from '../../services/payroll.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';
import {
    IRecordPaymentRequest,
    SalaryPaymentMethod,
} from '../../interfaces/salaryPayment';

interface Props {
    open: boolean;
    onClose: () => void;
    payrollId: string;
    netSalary: number;
    outstanding: number;
    onSuccess: () => void;
}

const METHODS: SalaryPaymentMethod[] = ['CASH', 'BANK_TRANSFER', 'MOBILE_BANKING', 'CHEQUE', 'OTHER'];

// Positive decimal with up to 2 decimal places. Disallows leading/trailing spaces
// (caller trims before validating) and requires at least one digit.
const AMOUNT_REGEX = /^\d+(\.\d{1,2})?$/;

const todayIso = () => new Date().toISOString().substring(0, 10);

const PaymentFormDialog: React.FC<Props> = ({ open, onClose, payrollId, netSalary, outstanding, onSuccess }) => {
    const { t } = useTranslation(['payroll', 'common']);
    const { showSuccess, showApiError } = useToast();
    const [submitting, setSubmitting] = useState(false);

    const [amount, setAmount] = useState<string>('');
    const [amountError, setAmountError] = useState<string>('');
    const [paymentDate, setPaymentDate] = useState<string>(todayIso());
    const [paymentMethod, setPaymentMethod] = useState<SalaryPaymentMethod>('CASH');

    const [bankName, setBankName] = useState('');
    const [bankAccountNumber, setBankAccountNumber] = useState('');
    const [transactionId, setTransactionId] = useState('');

    const [mobileProvider, setMobileProvider] = useState('');
    const [mobileAccount, setMobileAccount] = useState('');

    const [chequeNumber, setChequeNumber] = useState('');
    const [chequeBank, setChequeBank] = useState('');
    const [chequeDate, setChequeDate] = useState<string>('');

    const [notes, setNotes] = useState('');
    const [reason, setReason] = useState('');

    useEffect(() => {
        if (open) {
            setAmount(outstanding > 0 ? outstanding.toFixed(2) : '');
            setPaymentDate(todayIso());
            setPaymentMethod('CASH');
            setBankName('');
            setBankAccountNumber('');
            setTransactionId('');
            setMobileProvider('');
            setMobileAccount('');
            setChequeNumber('');
            setChequeBank('');
            setChequeDate('');
            setNotes('');
            setReason('');
            setAmountError('');
        }
    }, [open, outstanding]);

    const handleSubmit = async () => {
        const trimmed = amount.trim();
        if (!AMOUNT_REGEX.test(trimmed) || trimmed === '0' || trimmed === '0.0' || trimmed === '0.00') {
            setAmountError(t('paymentForm.amountError'));
            return;
        }
        setAmountError('');

        const req: IRecordPaymentRequest = {
            amount: trimmed,
            paymentDate,
            paymentMethod,
            notes: notes || null,
            reason: reason || null,
        };

        if (paymentMethod === 'BANK_TRANSFER') {
            req.bankName = bankName || null;
            req.bankAccountNumber = bankAccountNumber || null;
            req.transactionId = transactionId || null;
        } else if (paymentMethod === 'MOBILE_BANKING') {
            req.mobileProvider = mobileProvider || null;
            req.mobileAccount = mobileAccount || null;
            req.transactionId = transactionId || null;
        } else if (paymentMethod === 'CHEQUE') {
            req.chequeNumber = chequeNumber || null;
            req.chequeBank = chequeBank || null;
            req.chequeDate = chequeDate || null;
        }

        setSubmitting(true);
        try {
            await PayrollService.recordPayment(payrollId, req);
            showSuccess(t('paymentForm.toast.recorded'));
            onSuccess();
            onClose();
        } catch (error) {
            showApiError(error, t('paymentForm.toast.failed'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{t('paymentForm.title')}</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                        {t('paymentForm.summary', { net: netSalary.toFixed(2), outstanding: outstanding.toFixed(2) })}
                    </Typography>

                    <TextField
                        label={t('common:fields.amount')}
                        type="text"
                        value={amount}
                        onChange={(e) => {
                            setAmount(e.target.value);
                            if (amountError) setAmountError('');
                        }}
                        inputProps={{ inputMode: 'decimal' }}
                        required
                        fullWidth
                        error={!!amountError}
                        helperText={amountError || t('paymentForm.amountHelper')}
                    />

                    <TextField
                        label={t('shared.paymentDate')}
                        type="date"
                        value={paymentDate}
                        onChange={(e) => setPaymentDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        required
                        fullWidth
                    />

                    <FormControl fullWidth required>
                        <InputLabel id="payment-method-label">{t('shared.paymentMethod')}</InputLabel>
                        <Select
                            labelId="payment-method-label"
                            label={t('shared.paymentMethod')}
                            value={paymentMethod}
                            onChange={(e) => setPaymentMethod(e.target.value as SalaryPaymentMethod)}
                            inputProps={{ 'aria-label': t('shared.paymentMethod') }}
                        >
                            {METHODS.map(m => <MenuItem key={m} value={m}>{t(`paymentMethod.${m}`)}</MenuItem>)}
                        </Select>
                    </FormControl>

                    {paymentMethod === 'BANK_TRANSFER' && (
                        <>
                            <TextField
                                label={t('shared.bankName')}
                                value={bankName}
                                onChange={(e) => setBankName(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label={t('paymentForm.bankAccountNumber')}
                                value={bankAccountNumber}
                                onChange={(e) => setBankAccountNumber(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label={t('paymentForm.transactionId')}
                                value={transactionId}
                                onChange={(e) => setTransactionId(e.target.value)}
                                fullWidth
                            />
                        </>
                    )}

                    {paymentMethod === 'MOBILE_BANKING' && (
                        <>
                            <TextField
                                label={t('paymentForm.mobileProvider')}
                                value={mobileProvider}
                                onChange={(e) => setMobileProvider(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label={t('paymentForm.mobileAccount')}
                                value={mobileAccount}
                                onChange={(e) => setMobileAccount(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label={t('paymentForm.transactionId')}
                                value={transactionId}
                                onChange={(e) => setTransactionId(e.target.value)}
                                fullWidth
                            />
                        </>
                    )}

                    {paymentMethod === 'CHEQUE' && (
                        <>
                            <TextField
                                label={t('paymentForm.chequeNumber')}
                                value={chequeNumber}
                                onChange={(e) => setChequeNumber(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label={t('paymentForm.chequeBank')}
                                value={chequeBank}
                                onChange={(e) => setChequeBank(e.target.value)}
                                fullWidth
                            />
                            <TextField
                                label={t('paymentForm.chequeDate')}
                                type="date"
                                value={chequeDate}
                                onChange={(e) => setChequeDate(e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                fullWidth
                            />
                        </>
                    )}

                    <TextField
                        label={t('common:fields.notes')}
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        multiline
                        rows={2}
                        fullWidth
                    />

                    <TextField
                        label={t('paymentForm.reasonLabel')}
                        value={reason}
                        onChange={(e) => setReason(e.target.value)}
                        fullWidth
                        helperText={t('paymentForm.reasonHelper')}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>{t('common:actions.cancel')}</Button>
                <Button onClick={handleSubmit} variant="contained" disabled={submitting}>
                    {submitting ? t('shared.saving') : t('paymentForm.record')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default PaymentFormDialog;
